import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ICON } from './icons';
import { Svg } from './Svg';

/* Меню «⋯» блока и тренировки (openKebab в прототипе). Редкие действия — заметка, сохранить в базу,
   в текст, очистить, удалить — за одной кнопкой: пункты подписаны словами, опасные — красным под чертой.
   Закрывается выбором пункта, кликом мимо, Esc и повторным нажатием на «⋯». */
export interface KebabItem {
  k: string;
  icon: string;
  label: string;
  /** done — уже сделано (серым), rm — опасное (красным, под чертой) */
  cls?: 'done' | 'rm';
  onClick: () => void;
}

export function Kebab({ items, title, id }: { items: (KebabItem | null | false)[]; title: string; id?: string }) {
  const btn = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const list = items.filter(Boolean) as KebabItem[];

  /* Как в прототипе: правым краем к кнопке, под ней; у края окна — сдвиг внутрь */
  useLayoutEffect(() => {
    if (!open || !btn.current || !box.current) return;
    const r = btn.current.getBoundingClientRect();
    const w = box.current.offsetWidth;
    setPos({ left: Math.max(12, Math.min(r.right - w, document.documentElement.clientWidth - w - 12)), top: r.bottom + window.scrollY + 6 });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const out = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!box.current?.contains(t) && !btn.current?.contains(t)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', out);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', out);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  const rows = (xs: KebabItem[]) =>
    xs.map((it) => (
      <button
        key={it.k}
        className={'row ' + (it.cls || '')}
        data-km={it.k}
        role="menuitem"
        onClick={() => {
          setOpen(false);
          it.onClick();
        }}
      >
        <Svg src={it.icon} />
        <b>{it.label}</b>
      </button>
    ));
  const safe = list.filter((x) => x.cls !== 'rm');
  const danger = list.filter((x) => x.cls === 'rm');

  return (
    <>
      <button ref={btn} className={'x kebab' + (open ? ' open' : '')} id={id} title={title} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <Svg src={ICON.more} />
      </button>
      {open &&
        createPortal(
          <div ref={box} className="sug kmenu" role="menu" style={pos ?? { left: -9999, top: 0 }}>
            {rows(safe)}
            {safe.length > 0 && danger.length > 0 && <i className="sep"></i>}
            {rows(danger)}
          </div>,
          document.body,
        )}
    </>
  );
}
